/**
 * Protocol Buffers 바이너리 메시지 디코더
 * 서버에서 전송하는 바이너리 WebSocket 메시지를 디코딩합니다.
 *
 * 메시지 구조:
 * [타입코드(1byte)][Protocol Buffers 데이터(N bytes)]
 *
 * 부동소수점 오차 해결:
 * 서버에서 부동소수점 필드를 string으로 전송하는 필드는 decimal.js를 사용하여
 * 정확한 number 타입으로 변환합니다.
 *
 * 변환되는 필드 (proto에서 string으로 정의된 필드만):
 * - MarketQuote/Quote: buy, sell, buySize, sellSize (string으로 정의됨, number로 변환 필요)
 * - OrderReceived: orderQuantity, barrierPrice, orderPrice, profitRealizationBarrierPrice,
 *                  lossCutBarrierPrice, orderBalance, depositAssets, totalMargin
 * - OrderRejected: rejectQuantity, depositAssets, totalMargin
 * - OrderExecuted: executionQuantity, executionPrice, depositAssets, totalMargin
 * - BalanceUpdated: bookQuantity, liquidationPossibleQuantity, bookPrice, currentPrice,
 *                   evaluationProfitLoss
 * - DepositUpdated: depositAssets
 * - OrderClosed: closeOutQuantity, purchasePrice, closeOutPrice, closeOutProfitLoss,
 *                executionCharge, swapCharge
 *
 * 참고: string으로 정의되지 않은 필드(예: timestamp, accountNo 등)는 변환하지 않습니다.
 */
import { MarketQuoteList, MarketOrderBook, OrderBookCancel, MarketTicker, OrderReceived, OrderRejected, OrderExecuted, BalanceUpdated, DepositUpdated, OrderClosed, } from '../proto/realtime_message.js';
import Decimal from 'decimal.js';
/**
 * Protocol Buffers 메시지 타입 코드
 * 서버의 ProtoMessageType enum과 일치해야 함
 */
export var ProtoMessageType;
(function (ProtoMessageType) {
    ProtoMessageType[ProtoMessageType["MARKET_QUOTE_LIST"] = 1] = "MARKET_QUOTE_LIST";
    ProtoMessageType[ProtoMessageType["MARKET_ORDER_BOOK"] = 2] = "MARKET_ORDER_BOOK";
    ProtoMessageType[ProtoMessageType["MARKET_ORDER_BOOK_CANCEL"] = 3] = "MARKET_ORDER_BOOK_CANCEL";
    ProtoMessageType[ProtoMessageType["ORDER_ACCEPTED"] = 4] = "ORDER_ACCEPTED";
    ProtoMessageType[ProtoMessageType["ORDER_REJECTED"] = 5] = "ORDER_REJECTED";
    ProtoMessageType[ProtoMessageType["ORDER_EXECUTED"] = 6] = "ORDER_EXECUTED";
    ProtoMessageType[ProtoMessageType["ACCOUNT_BALANCE_UPDATE"] = 7] = "ACCOUNT_BALANCE_UPDATE";
    ProtoMessageType[ProtoMessageType["DEPOSIT_UPDATED"] = 8] = "DEPOSIT_UPDATED";
    ProtoMessageType[ProtoMessageType["MARKET_TICKER"] = 9] = "MARKET_TICKER";
    ProtoMessageType[ProtoMessageType["ORDER_CLOSED"] = 10] = "ORDER_CLOSED";
})(ProtoMessageType || (ProtoMessageType = {}));
/**
 * Protocol Buffers 바이너리 메시지 디코더 클래스
 */
export class BinaryMessageDecoder {
    /**
     * string 또는 number를 정확한 number로 변환
     * 부동소수점 오차를 방지하기 위해 decimal.js를 사용합니다.
     *
     * 변환 과정:
     * 1. 서버에서 string으로 전송 (proto에서 string 타입)
     * 2. Protocol Buffers 디코딩: string 그대로 유지 (값 변경 없음)
     * 3. decimal.js로 정확한 변환: string → Decimal → number
     *
     * 참고: decimal.js는 정확한 십진수 연산을 보장하지만,
     * toNumber()는 JavaScript number로 변환하므로 매우 큰 수나 매우 작은 수의 경우
     * 정밀도 손실이 발생할 수 있습니다. 일반적인 금융 데이터 범위에서는 문제 없습니다.
     *
     * @param value - 변환할 값 (string | number | null | undefined)
     * @returns 변환된 number 값 (값이 null/undefined인 경우 0)
     */
    static convertStringToNumber(value) {
        if (value === null || value === undefined) {
            return 0;
        }
        if (typeof value === 'number') {
            return value;
        }
        if (typeof value === 'string') {
            // 빈 문자열인 경우 0 반환
            if (value.trim() === '') {
                return 0;
            }
            // decimal.js를 사용하여 정확한 변환
            // Decimal 객체를 통해 정확한 십진수 연산 후 number로 변환
            return new Decimal(value).toNumber();
        }
        return Number(value);
    }
    /**
     * 메시지 타입 코드로 메시지 타입명 반환
     */
    static getMessageTypeName(typeCode) {
        const typeMap = {
            [ProtoMessageType.MARKET_QUOTE_LIST]: 'MARKET_QUOTE_LIST',
            [ProtoMessageType.MARKET_ORDER_BOOK]: 'MARKET_ORDER_BOOK',
            [ProtoMessageType.MARKET_ORDER_BOOK_CANCEL]: 'MARKET_ORDER_BOOK_CANCEL',
            [ProtoMessageType.ORDER_ACCEPTED]: 'ORDER_ACCEPTED',
            [ProtoMessageType.ORDER_REJECTED]: 'ORDER_REJECTED',
            [ProtoMessageType.ORDER_EXECUTED]: 'ORDER_EXECUTED',
            [ProtoMessageType.ACCOUNT_BALANCE_UPDATE]: 'ACCOUNT_BALANCE_UPDATE',
            [ProtoMessageType.DEPOSIT_UPDATED]: 'DEPOSIT_UPDATED',
            [ProtoMessageType.MARKET_TICKER]: 'MARKET_TICKER',
            [ProtoMessageType.ORDER_CLOSED]: 'ORDER_CLOSED',
        };
        return typeMap[typeCode] || 'UNKNOWN';
    }
    /**
     * MarketQuoteList 디코딩
     * proto에서 string으로 정의된 필드(buy, sell, buySize, sellSize)를 number로 변환합니다.
     */
    static decodeMarketQuoteList(protoData) {
        try {
            const decoded = MarketQuoteList.decode(protoData);
            const plain = MarketQuoteList.toObject(decoded, {
                longs: Number,
                defaults: true,
            });
            // 원본 값 확인 로그 (변환 전)
            // if (plain.quotes && plain.quotes.length > 0) {
            //   const firstQuote = plain.quotes[0];
            //   if (firstQuote.stockCd === 'AUDCAD') {
            //     console.error('━━━━━━━━━━QUOTE Decode 전 원본━━━━━━━━━━━━');
            //     console.error('  buy 원본:', firstQuote.buy, '타입:', typeof firstQuote.buy);
            //     console.error('  sell 원본:', firstQuote.sell, '타입:', typeof firstQuote.sell);
            //   }
            // }
            // proto에서 string으로 정의된 필드를 number로 변환 (부동소수점 오차 방지)
            if (plain.quotes) {
                plain.quotes = plain.quotes.map((quote) => ({
                    stockCd: quote.stockCd,
                    buy: this.convertStringToNumber(quote.buy),
                    sell: this.convertStringToNumber(quote.sell),
                    buySize: this.convertStringToNumber(quote.buySize),
                    sellSize: this.convertStringToNumber(quote.sellSize),
                    timestamp: quote.timestamp,
                }));
            }
            return plain;
        }
        catch (error) {
            console.error('[BinaryMessageDecoder] MarketQuoteList 디코딩 실패:', error);
            throw error;
        }
    }
    /**
     * MarketOrderBook 디코딩
     * proto에서 string으로 정의된 필드(buy, sell, buySize, sellSize)를 number로 변환합니다.
     */
    static decodeMarketOrderBook(protoData) {
        try {
            const decoded = MarketOrderBook.decode(protoData);
            const plain = MarketOrderBook.toObject(decoded, {
                longs: Number,
                defaults: true,
            });
            // 원본 값 확인 로그 (변환 전)
            // if (plain.quotes && plain.quotes.length > 0 && plain.stockCd === 'AUDCAD') {
            //   const firstQuote = plain.quotes[0]; // 1호가
            //   console.error('━━━━━━━━━ORDERBOOK Decode 전 원본 ━━━━━━━━━━━');
            //   console.error('  buy 원본(1호가):', firstQuote.buy, '타입:', typeof firstQuote.buy);
            //   console.error('  sell 원본(1호가):', firstQuote.sell, '타입:', typeof firstQuote.sell);
            // }
            // proto에서 string으로 정의된 필드를 number로 변환 (부동소수점 오차 방지)
            const result = {
                stockCd: plain.stockCd,
                quotes: plain.quotes
                    ? plain.quotes.map((quote) => ({
                        buy: this.convertStringToNumber(quote.buy),
                        sell: this.convertStringToNumber(quote.sell),
                        buySize: this.convertStringToNumber(quote.buySize),
                        sellSize: this.convertStringToNumber(quote.sellSize),
                    }))
                    : undefined,
                timestamp: plain.timestamp,
            };
            return result;
        }
        catch (error) {
            console.error('[BinaryMessageDecoder] MarketOrderBook 디코딩 실패:', error);
            throw error;
        }
    }
    /**
     * OrderBookCancel 디코딩
     */
    static decodeOrderBookCancel(protoData) {
        try {
            const decoded = OrderBookCancel.decode(protoData);
            const plain = OrderBookCancel.toObject(decoded, {
                longs: Number,
                defaults: true,
            });
            // toObject의 longs: Number 옵션으로 이미 number로 변환됨
            return plain;
        }
        catch (error) {
            console.error('[BinaryMessageDecoder] OrderBookCancel 디코딩 실패:', error);
            throw error;
        }
    }
    /**
     * OrderReceived (주문 접수) 디코딩
     * proto에서 string으로 정의된 필드(orderQuantity, barrierPrice, orderPrice,
     * profitRealizationBarrierPrice, lossCutBarrierPrice, orderBalance, depositAssets, totalMargin, purchasePrice)만 number로 변환합니다.
     */
    static decodeOrderReceived(protoData) {
        try {
            const decoded = OrderReceived.decode(protoData);
            const plain = OrderReceived.toObject(decoded, {
                longs: Number,
                defaults: true,
            });
            // proto에서 string으로 정의된 필드만 number로 변환 (부동소수점 오차 방지)
            return {
                accountNo: plain.accountNo,
                orderDateTime: plain.orderDateTime,
                orderNo: plain.orderNo,
                stockCd: plain.stockCd,
                orderVersionCd: plain.orderVersionCd,
                positionCd: plain.positionCd,
                orderTypeCd: plain.orderTypeCd,
                sideCd: plain.sideCd,
                orderCd: plain.orderCd,
                orderQuantity: this.convertStringToNumber(plain.orderQuantity),
                barrierPrice: this.convertStringToNumber(plain.barrierPrice),
                orderPrice: this.convertStringToNumber(plain.orderPrice),
                profitRealizationBarrierPrice: this.convertStringToNumber(plain.profitRealizationBarrierPrice),
                lossCutBarrierPrice: this.convertStringToNumber(plain.lossCutBarrierPrice),
                orderBalance: this.convertStringToNumber(plain.orderBalance),
                orderStatusCd: plain.orderStatusCd,
                isProfitExecution: plain.isProfitExecution,
                isLossLimits: plain.isLossLimits,
                isLossTracing: plain.isLossTracing,
                purchaseDateTime: plain.purchaseDateTime,
                balanceNo: plain.balanceNo,
                depositAssets: this.convertStringToNumber(plain.depositAssets),
                totalMargin: this.convertStringToNumber(plain.totalMargin),
                isTickerMessage: plain.isTickerMessage,
                purchasePrice: this.convertStringToNumber(plain.purchasePrice),
                // Trailing Stop: 최초손실추적가격, 최초손실제한가격 (string으로 유지, WebSocketService에서 number로 변환)
                firstLossTrackingPrice: plain.firstLossTrackingPrice,
                firstLossCutPrice: plain.firstLossCutPrice,
            };
        }
        catch (error) {
            console.error('[BinaryMessageDecoder] OrderReceived 디코딩 실패:', error);
            throw error;
        }
    }
    /**
     * OrderRejected (주문 거부) 디코딩
     * proto에서 string으로 정의된 필드(rejectQuantity, depositAssets, totalMargin)만 number로 변환합니다.
     */
    static decodeOrderRejected(protoData) {
        try {
            const decoded = OrderRejected.decode(protoData);
            const plain = OrderRejected.toObject(decoded, {
                longs: Number,
                defaults: true,
            });
            // proto에서 string으로 정의된 필드만 number로 변환 (부동소수점 오차 방지)
            return {
                accountNo: plain.accountNo,
                orderDateTime: plain.orderDateTime,
                orderNo: plain.orderNo,
                stockCd: plain.stockCd,
                positionCd: plain.positionCd,
                sideCd: plain.sideCd,
                rejectQuantity: this.convertStringToNumber(plain.rejectQuantity),
                purchaseDateTime: plain.purchaseDateTime,
                balanceNo: plain.balanceNo,
                depositAssets: this.convertStringToNumber(plain.depositAssets),
                totalMargin: this.convertStringToNumber(plain.totalMargin),
                orderCd: plain.orderCd,
            };
        }
        catch (error) {
            console.error('[BinaryMessageDecoder] OrderRejected 디코딩 실패:', error);
            throw error;
        }
    }
    /**
     * OrderExecuted (주문 체결) 디코딩
     * proto에서 string으로 정의된 필드(executionQuantity, executionPrice, depositAssets, totalMargin)만 number로 변환합니다.
     */
    static decodeOrderExecuted(protoData) {
        try {
            const decoded = OrderExecuted.decode(protoData);
            const plain = OrderExecuted.toObject(decoded, {
                longs: Number,
                defaults: true,
            });
            // proto에서 string으로 정의된 필드만 number로 변환 (부동소수점 오차 방지)
            return {
                accountNo: plain.accountNo,
                orderDateTime: plain.orderDateTime,
                orderNo: plain.orderNo,
                executionQuantity: this.convertStringToNumber(plain.executionQuantity),
                executionPrice: this.convertStringToNumber(plain.executionPrice),
                depositAssets: this.convertStringToNumber(plain.depositAssets),
                totalMargin: this.convertStringToNumber(plain.totalMargin),
            };
        }
        catch (error) {
            console.error('[BinaryMessageDecoder] OrderExecuted 디코딩 실패:', error);
            throw error;
        }
    }
    /**
     * BalanceUpdated (잔고 수정) 디코딩
     * proto에서 string으로 정의된 필드(bookQuantity, liquidationPossibleQuantity,
     * bookPrice, currentPrice, evaluationProfitLoss)만 number로 변환합니다.
     */
    static decodeBalanceUpdated(protoData) {
        try {
            const decoded = BalanceUpdated.decode(protoData);
            const plain = BalanceUpdated.toObject(decoded, {
                longs: Number,
                defaults: true,
            });
            // proto에서 string으로 정의된 필드만 number로 변환 (부동소수점 오차 방지)
            return {
                accountNo: plain.accountNo,
                orderDateTime: plain.orderDateTime,
                orderNo: plain.orderNo,
                stockCd: plain.stockCd,
                tradeCurrencyCd: plain.tradeCurrencyCd,
                positionCd: plain.positionCd,
                purchaseDateTime: plain.purchaseDateTime,
                bookQuantity: this.convertStringToNumber(plain.bookQuantity),
                liquidationPossibleQuantity: this.convertStringToNumber(plain.liquidationPossibleQuantity),
                bookPrice: this.convertStringToNumber(plain.bookPrice),
                currentPrice: this.convertStringToNumber(plain.currentPrice),
                evaluationProfitLoss: this.convertStringToNumber(plain.evaluationProfitLoss),
                stockGroupCd: plain.stockGroupCd,
                balanceNo: plain.balanceNo,
            };
        }
        catch (error) {
            console.error('[BinaryMessageDecoder] BalanceUpdated 디코딩 실패:', error);
            throw error;
        }
    }
    /**
     * DepositUpdated (예수금 수정) 디코딩
     * proto에서 string으로 정의된 필드(depositAssets)만 number로 변환합니다.
     */
    static decodeDepositUpdated(protoData) {
        try {
            const decoded = DepositUpdated.decode(protoData);
            const plain = DepositUpdated.toObject(decoded, {
                longs: Number,
                defaults: true,
            });
            // proto에서 string으로 정의된 필드만 number로 변환 (부동소수점 오차 방지)
            return {
                accountNo: plain.accountNo,
                depositAssets: this.convertStringToNumber(plain.depositAssets),
            };
        }
        catch (error) {
            console.error('[BinaryMessageDecoder] DepositUpdated 디코딩 실패:', error);
            throw error;
        }
    }
    /**
     * MarketTicker (시장 티커) 디코딩
     * proto에서 string으로 정의된 필드(open, high, low, close, beforeClose)를 number로 변환합니다.
     */
    static decodeMarketTicker(protoData) {
        try {
            const decoded = MarketTicker.decode(protoData);
            const plain = MarketTicker.toObject(decoded, {
                longs: Number,
                defaults: true,
            });
            // proto에서 string으로 정의된 필드만 number로 변환 (부동소수점 오차 방지)
            return {
                stockCd: plain.stockCd,
                date: plain.date,
                open: this.convertStringToNumber(plain.open),
                high: this.convertStringToNumber(plain.high),
                low: this.convertStringToNumber(plain.low),
                close: this.convertStringToNumber(plain.close),
                beforeClose: this.convertStringToNumber(plain.beforeClose),
                lastUpdated: plain.lastUpdated,
            };
        }
        catch (error) {
            console.error('[BinaryMessageDecoder] MarketTicker 디코딩 실패:', error);
            throw error;
        }
    }
    /**
     * OrderClosed (청산 완료) 디코딩
     * proto에서 string으로 정의된 필드(closeOutQuantity, purchasePrice, closeOutPrice,
     * closeOutProfitLoss, executionCharge, swapCharge)를 number로 변환합니다.
     */
    static decodeOrderClosed(protoData) {
        try {
            const decoded = OrderClosed.decode(protoData);
            const plain = OrderClosed.toObject(decoded, {
                longs: Number,
                defaults: true,
            });
            // proto에서 string으로 정의된 필드만 number로 변환 (부동소수점 오차 방지)
            return {
                accountNo: plain.accountNo,
                purchaseDateTime: plain.purchaseDateTime,
                orderNo: plain.orderNo,
                executionDateTime: plain.executionDateTime,
                stockCd: plain.stockCd,
                tradeCurrencyCd: plain.tradeCurrencyCd,
                positionCd: plain.positionCd,
                closeOutQuantity: this.convertStringToNumber(plain.closeOutQuantity),
                purchasePrice: this.convertStringToNumber(plain.purchasePrice),
                closeOutPrice: this.convertStringToNumber(plain.closeOutPrice),
                closeOutProfitLoss: this.convertStringToNumber(plain.closeOutProfitLoss),
                executionCharge: this.convertStringToNumber(plain.executionCharge),
                swapCharge: this.convertStringToNumber(plain.swapCharge),
            };
        }
        catch (error) {
            console.error('[BinaryMessageDecoder] OrderClosed 디코딩 실패:', error);
            throw error;
        }
    }
    /**
     * 통합 디코딩 함수
     * @param data - ArrayBuffer 또는 Uint8Array
     * @returns 디코딩된 메시지 객체
     */
    static decode(data) {
        // ArrayBuffer를 Uint8Array로 변환
        const uint8Array = data instanceof ArrayBuffer ? new Uint8Array(data) : data;
        // 메시지가 너무 짧으면 오류
        if (uint8Array.length < 2) {
            throw new Error(`메시지가 너무 짧습니다: ${uint8Array.length} bytes`);
        }
        // 첫 번째 바이트 = 메시지 타입 코드
        const typeCode = uint8Array[0];
        // 나머지 바이트 = 실제 Protocol Buffers 데이터
        const protoData = uint8Array.slice(1);
        // 메시지 타입명
        const messageType = this.getMessageTypeName(typeCode);
        // 타입 코드에 따라 적절한 메시지로 디코딩
        switch (typeCode) {
            case ProtoMessageType.MARKET_QUOTE_LIST:
                return {
                    messageType,
                    typeCode,
                    data: this.decodeMarketQuoteList(protoData),
                };
            case ProtoMessageType.MARKET_ORDER_BOOK:
                return {
                    messageType,
                    typeCode,
                    data: this.decodeMarketOrderBook(protoData),
                };
            case ProtoMessageType.MARKET_ORDER_BOOK_CANCEL:
                return {
                    messageType,
                    typeCode,
                    data: this.decodeOrderBookCancel(protoData),
                };
            case ProtoMessageType.ORDER_ACCEPTED: {
                const decodedData = this.decodeOrderReceived(protoData);
                // console.warn('[DEBUG] 실시간 접수 수신', decodedData);
                return {
                    messageType,
                    typeCode,
                    data: decodedData,
                };
            }
            case ProtoMessageType.ORDER_REJECTED: {
                const decodedData = this.decodeOrderRejected(protoData);
                // console.warn('[DEBUG] 실시간 거부 수신', decodedData);
                return {
                    messageType,
                    typeCode,
                    data: decodedData,
                };
            }
            case ProtoMessageType.ORDER_EXECUTED: {
                const decodedData = this.decodeOrderExecuted(protoData);
                // console.warn('[DEBUG] 실시간 체결 수신', decodedData);
                return {
                    messageType,
                    typeCode,
                    data: decodedData,
                };
            }
            case ProtoMessageType.ACCOUNT_BALANCE_UPDATE: {
                const decodedData = this.decodeBalanceUpdated(protoData);
                // console.warn('[DEBUG] 실시간 잔고 수신', decodedData);
                return {
                    messageType,
                    typeCode,
                    data: decodedData,
                };
            }
            case ProtoMessageType.DEPOSIT_UPDATED: {
                const decodedData = this.decodeDepositUpdated(protoData);
                // console.warn('[DEBUG] 실시간 예수금 수신', decodedData);
                return {
                    messageType,
                    typeCode,
                    data: decodedData,
                };
            }
            case ProtoMessageType.ORDER_CLOSED:
                return {
                    messageType,
                    typeCode,
                    data: this.decodeOrderClosed(protoData),
                };
            case ProtoMessageType.MARKET_TICKER:
                return {
                    messageType,
                    typeCode,
                    data: this.decodeMarketTicker(protoData),
                };
            default:
                throw new Error(`알 수 없는 메시지 타입 코드: ${typeCode}`);
        }
    }
    /**
     * Blob 데이터 디코딩 (비동기)
     */
    static async decodeBlob(blob) {
        const arrayBuffer = await blob.arrayBuffer();
        return this.decode(arrayBuffer);
    }
}
//# sourceMappingURL=BinaryMessageDecoder.js.map